import Link from "next/link";

import { Anchor } from "@newsela/angelou";
import { actionNames, objectTypes, withTrackEvent } from "@newsela/snitch";
import dayjs from "dayjs";

import BookmarkButton from "@/components/BookmarkButton";
import { contentCardBookmarkCss } from "@/search/NewSearchPageMVP/SearchResults/ContentCard/ContentCard.styles";
import { STANDARD_CARD_YEAR_FORMAT } from "@/search/NewSearchPageMVP/SearchResults/StandardCard/StandardCard.constants";
import {
  standardCardAnchorCss,
  standardCardCodeCss,
  standardCardContainerCss,
  standardCardDateCss,
  standardCardDatesContainer,
  standardCardDescriptionCss,
  standardCardGradeLevelsCss,
  standardCardRelativeCss,
  standardCardTitleCss,
} from "@/search/NewSearchPageMVP/SearchResults/StandardCard/StandardCard.styles";
import { getStandardMinMaxGrade } from "@/search/NewSearchPageMVP/SearchResults/StandardCard/StandardCard.utils";
import { ISearchResult } from "@/search/NewSearchPageMVP/types/ISearchResult";

const AnchorWithTrackEvent = withTrackEvent(Anchor);

const StandardCard = ({ content }: IStandardCard) => {
  const {
    grades = [],
    content_id: contentId,
    date_created: dateCreated,
    date_published: datePublished,
    number_enhanced: numberEnhanced,
    statement_combined_description: statementCombinedDescription,
  } = content;

  return (
    <article css={standardCardRelativeCss} data-testid="standards-card">
      <AnchorWithTrackEvent
        href={`/standards/${contentId}`}
        __cssFor={standardCardAnchorCss}
        actionName={actionNames.navigate}
        objectType={objectTypes.content}
        ariaProps={{ "aria-label": `View standard card ${contentId}` }}
        __renderCustomLink={({ children, href, ...anchorProps }) => {
          return (
            <Link href={href as URL} passHref legacyBehavior>
              <a {...anchorProps}>{children}</a>
            </Link>
          );
        }}
      />

      <div css={standardCardContainerCss}>
        <div>
          <div css={standardCardTitleCss}>Standard</div>

          <BookmarkButton
            contentId={contentId}
            __cssFor={contentCardBookmarkCss}
          />
        </div>

        <div css={standardCardDatesContainer}>
          <time dateTime={datePublished} css={standardCardDateCss}>
            {dayjs(datePublished).format(STANDARD_CARD_YEAR_FORMAT)}
          </time>

          {dateCreated && (
            <time dateTime={dateCreated} css={standardCardDateCss}>
              , revised {dayjs(dateCreated).format(STANDARD_CARD_YEAR_FORMAT)}
            </time>
          )}
        </div>

        <div css={standardCardCodeCss}>{numberEnhanced}</div>

        <div css={standardCardDescriptionCss}>
          {statementCombinedDescription}
        </div>

        {!!grades.length && (
          <div css={standardCardGradeLevelsCss}>
            {getStandardMinMaxGrade(grades)}
          </div>
        )}
      </div>
    </article>
  );
};

interface IStandardCard {
  content: ISearchResult;
}

StandardCard.displayName = "StandardCard";
export { StandardCard };
